// client/src/components/payment/checkout/StripeCheckoutSection.jsx
//
// Stripe-specific half of UnifiedCheckout. Wraps the same Elements +
// PaymentElement pattern used elsewhere (see PaymentMethodForm.jsx).
// On successful confirmation it returns the PaymentIntent id via onSuccess
// so the caller can POST it to the server's /purchase-confirm endpoint.

import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

function StripeInnerForm({ amountLabel, onSuccess, onError, busy, setBusy, submitButtonStyle }) {
  const stripe = useStripe();
  const elements = useElements();
  const [stripeError, setStripeError] = useState(null);

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || busy) return;
    setBusy(true);
    setStripeError(null);
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });
      if (error) {
        setStripeError(error.message || 'Payment could not be confirmed');
        setBusy(false);
        return;
      }
      if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess({ providerPaymentId: paymentIntent.id });
      } else {
        setStripeError(`Payment ${paymentIntent?.status || 'unknown'}`);
        setBusy(false);
      }
    } catch (err) {
      onError(err?.message || 'Unexpected payment error');
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleConfirm}>
      <PaymentElement />
      {stripeError && (
        <div style={{ color: '#dc2626', marginTop: 12, fontSize: 14 }}>{stripeError}</div>
      )}
      <button type="submit" disabled={!stripe || busy} style={submitButtonStyle(busy)}>
        {busy ? 'Processing…' : `Pay ${amountLabel}`}
      </button>
    </form>
  );
}

export default function StripeCheckoutSection({
  clientSecret, amountLabel, onSuccess, onError, busy, setBusy, submitButtonStyle,
}) {
  if (!clientSecret) {
    return (
      <div style={{ color: '#dc2626' }}>Stripe checkout missing clientSecret.</div>
    );
  }
  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night' } }}>
      <StripeInnerForm
        amountLabel={amountLabel}
        onSuccess={onSuccess}
        onError={onError}
        busy={busy}
        setBusy={setBusy}
        submitButtonStyle={submitButtonStyle}
      />
    </Elements>
  );
}
